import { Injectable, inject } from '@angular/core';
import { ICallableObject, IRequestCallback, IRequestCallbackFunction, RequestService } from '@df/api/request/request.service';
import { AbstractFinder } from '@df/catalog/abstract-finder/abstract-finder';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Resource } from '@df/catalog/resource';
import { App } from '@df/core/app';
import { CacheService } from '@df/core/cache/cache';
import { Status } from '@df/core/status';
import compact from 'lodash-es/compact';
import defaults from 'lodash-es/defaults';
import isString from 'lodash-es/isString';
import map from 'lodash-es/map';

@Injectable()
export abstract class AbstractFinderService<T extends AbstractFinder, D extends Catalog.IFinderData> {
    protected _allLoaded: boolean = false;
    protected _loadedVerbosity = 0;

    protected abstract type: string;
    protected abstract requestPath: string;

    protected requestService = inject(RequestService);

    protected cacheService = inject(CacheService);

    protected app = inject(App);

    abstract resource: Resource<T, number>;

    locate(
        status: Status | undefined,
        findOptions: string | Catalog.IFindableLocateOptions | Catalog.IFindableLocateCoordsOptions,
        onSuccess?: IRequestCallbackFunction,
        onError?: IRequestCallback
    ): Promise<T[]> | void | boolean {
        const request: ICallableObject = {
            path: this.requestPath,
            data: isString(findOptions) ? { address: findOptions } : findOptions
        };

        if (
            ((request.data as Catalog.IFindableLocateOptions).address &&
                (request.data as Catalog.IFindableLocateOptions).address.length > 2) ||
            ((request.data as Catalog.IFindableLocateCoordsOptions).latitude &&
                (request.data as Catalog.IFindableLocateCoordsOptions).longitude)
        ) {
            request.data = defaults(request.data, {
                limit: 30,
                maxDistance: 200
                // country: app.data.defaultCountry // do not restore!, pass country from outside
            });

            const cName = `finderService.locate${this.type}`;

            const prepare = (results): T[] =>
                map(this.extractModelsFromResult(results), (findableData: D) => {
                    const model = this.resource.getCreate(findableData.id);
                    model.setData({ ...findableData });
                    if (model.data) {
                        model.data.distance = findableData.distance;
                    }
                    return model;
                });

            const cached = this.cacheService.get([cName, request]);

            if (cached) {
                const results = prepare(cached);
                if (onSuccess) {
                    onSuccess(results);
                }
                return new Promise(resolve => {
                    resolve(results);
                });
            } else {
                const _onSuccess = results => {
                    this.cacheService.set([cName, request], results);
                    return prepare(results);
                };
                return this.requestService.send(status, request, compact([_onSuccess, onSuccess]), onError, { api: 'node' });
            }
        }
        return false;
    }

    loadAll(status?: Status, verbosity: number = 1, onSuccess?: IRequestCallbackFunction, onError?: IRequestCallback): Promise<T[]> {
        if (this._allLoaded && this._loadedVerbosity <= verbosity) {
            return new Promise(resolve => {
                if (onSuccess) {
                    onSuccess();
                }
                resolve(this.resource.getAllExisting());
            });
        }

        const loadedIds = this.resource.toIds(this.resource.getAllExisting());

        return this.requestService.send(status, async () => {
            await this.resource.find({
                verbosity,
                filter: { verbosity, id: { $nin: loadedIds } }
            });

            if (onSuccess) {
                onSuccess();
            }

            this._allLoaded = true;
            this._loadedVerbosity = verbosity;
        });
    }

    extractModelsFromResult(result: any): D[] {
        return result.stores;
    }
}
